/**
 * GenericChecklistExecutionView.js
 *
 * Generic execution view for any checklist type.
 * Based on DailyCheckExecutionView pattern.
 *
 * UPDATED: 2025-01-10
 * - Removed two-tier system (simplified to flat lists)
 * - Checklists now display all items directly
 */

import React, { useState, useCallback, useMemo, useEffect } from 'react';
import {
  X,
  Check,
  CheckCircle,
  AlertTriangle,
  User,
  Calendar,
  Clock,
  Ship,
  MessageSquare,
  ChevronDown,
  ChevronUp,
  FileText,
  Info,
  HelpCircle
} from 'lucide-react';
import { useVessel } from '../../../../contexts/VesselContext';
import { usePermissions } from '../../../../hooks/usePermissions';
// FT-324 cycle 3: read the eagerly-mounted single instance from context.
import { useChecklistRecordsContext } from '../../../../hooks/domain/operations/ChecklistRecordsContext';
import { useVoyages } from '../../../../hooks/domain/operations/useVoyages';
import { useMainLogbook } from '../../../../hooks/domain/operations/useMainLogbook';
import { CHECKLIST_TYPES } from '../../../../schemas/checklistCompletionSchema';
import { portLabel } from '../../../../utils/voyage/portLabel';
import ContextLinkBanner from './ContextLinkBanner';
// FT-356: register onClose as the shell-level back handler.
import { useSetBackHandler } from '../../../../contexts/NavigationBackContext';


const GenericChecklistExecutionView = ({ checklist, onClose, onComplete }) => {
  useSetBackHandler(onClose);
  const { getCurrentVessel } = useVessel();
  const { currentUser } = usePermissions();
  const { saveCompletionRecord } = useChecklistRecordsContext();
  const { activeVoyage, addUnifiedLogEntry } = useVoyages();
  const { logChecklistCompletion } = useMainLogbook();
  const vessel = getCurrentVessel();

  // Get checklist items directly (no tier filtering)
  const items = useMemo(() => checklist.items || [], [checklist]);

  // Auto-populated context
  const now = useMemo(() => new Date(), []);
  const dateString = now.toLocaleDateString('en-GB', {
    weekday: 'long',
    year: 'numeric',
    month: 'long',
    day: 'numeric'
  });
  const timeString = now.toLocaleTimeString('en-GB', {
    hour: '2-digit',
    minute: '2-digit'
  });

  // Context linking - auto-detect if voyage is underway
  const [linkedContext, setLinkedContext] = useState(null);

  useEffect(() => {
    // Check if template allows voyage linking
    const canLinkToVoyage = !checklist.canLinkTo ||
                            checklist.canLinkTo.includes('voyage') ||
                            checklist.canLinkTo.length === 0;

    if (canLinkToVoyage && activeVoyage && activeVoyage.status === 'underway') {
      setLinkedContext({
        type: 'voyage',
        id: activeVoyage.voyageNumber || activeVoyage.id,
        name: `${portLabel(activeVoyage.from)} → ${portLabel(activeVoyage.to)}`,
        autoLinked: true,
        autoLogToLogbook: checklist.autoLogOnComplete !== false
      });
    }
  }, [activeVoyage, checklist.canLinkTo, checklist.autoLogOnComplete]);

  // State
  const [checkStates, setCheckStates] = useState(() => {
    const initial = {};
    items.forEach(item => {
      initial[item.id] = { passed: false, notes: '' };
    });
    return initial;
  });
  const [generalNotes, setGeneralNotes] = useState('');
  const [showNotes, setShowNotes] = useState({});
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [error, setError] = useState(null);

  // Validation
  const requiredItems = items.filter(item => item.required !== false);
  const allRequiredChecked = requiredItems.every(item => checkStates[item.id]?.passed);
  const completedCount = items.filter(item => checkStates[item.id]?.passed).length;

  // Handlers
  const handleToggleCheck = useCallback((itemId) => {
    setCheckStates(prev => ({
      ...prev,
      [itemId]: { ...prev[itemId], passed: !prev[itemId].passed }
    }));
  }, []);

  const handleItemNotes = useCallback((itemId, notes) => {
    setCheckStates(prev => ({
      ...prev,
      [itemId]: { ...prev[itemId], notes }
    }));
  }, []);

  const handleToggleNotes = useCallback((itemId) => {
    setShowNotes(prev => ({
      ...prev,
      [itemId]: !prev[itemId]
    }));
  }, []);

  const handleSubmit = useCallback(async () => {
    setIsSubmitting(true);
    setError(null);

    const checks = items.map(item => ({
      id: item.id,
      text: item.text,
      passed: checkStates[item.id].passed,
      notes: checkStates[item.id].notes || null,
      regulation: item.regulation || null
    }));

    const result = saveCompletionRecord({
      checklistType: checklist.checklistType || CHECKLIST_TYPES.OPERATIONAL,
      checklistId: checklist.id,
      checklistName: checklist.name,
      completedBy: {
        id: currentUser?.id || 'user-1',
        name: currentUser?.name || 'Crew Member',
        role: currentUser?.role || 'Crew'
      },
      checks,
      notes: generalNotes,
      linkedContext
    });

    if (result.success) {
      // Auto-log to Main Logbook if linkedContext requests it
      if (linkedContext?.autoLogToLogbook !== false) {
        try {
          await logChecklistCompletion(result.record);
          console.log('[GenericChecklistExecutionView] Logged to Main Logbook');
        } catch (logError) {
          console.warn('[GenericChecklistExecutionView] Failed to log to logbook:', logError);
          // Don't fail the checklist save if logging fails
        }
      }

      // FT-82: Add entry to active voyage's unified log if voyage is underway
      if (activeVoyage && activeVoyage.status === 'underway') {
        try {
          await addUnifiedLogEntry(activeVoyage.id, {
            type: 'checklist_completion',
            checklistId: checklist.id,
            checklistName: checklist.name,
            checklistType: checklist.checklistType || 'operational',
            completedBy: currentUser?.name || 'Crew Member',
            itemsCompleted: checks.length,
            itemsPassed: checks.filter(c => c.passed).length,
            notes: generalNotes || null,
            linkedContext: linkedContext || null
          });
          console.log('[GenericChecklistExecutionView] Added to Voyage Log');
        } catch (voyageLogError) {
          console.warn('[GenericChecklistExecutionView] Failed to add to voyage log:', voyageLogError);
          // Don't fail the checklist save if voyage logging fails
        }
      }

      onComplete?.(result.record);
      onClose();
    } else {
      setError(result.errors?.join(', ') || 'Failed to save');
      setIsSubmitting(false);
    }
  }, [checklist, items, checkStates, generalNotes, currentUser, linkedContext, saveCompletionRecord, logChecklistCompletion, onComplete, onClose]);

  // State for showing guidance
  const [showGuidance, setShowGuidance] = useState({});

  const handleToggleGuidance = useCallback((itemId) => {
    setShowGuidance(prev => ({
      ...prev,
      [itemId]: !prev[itemId]
    }));
  }, []);

  // Group items by phase if phases are defined, otherwise by category
  const groupedItems = useMemo(() => {
    const groups = {};
    const phaseOrder = checklist.phases || [];

    // Initialize groups in phase order
    phaseOrder.forEach(phaseId => {
      groups[phaseId] = [];
    });

    items.forEach(item => {
      // Use phase if defined, otherwise fall back to category
      const groupKey = item.phase || item.category || 'general';
      if (!groups[groupKey]) {
        groups[groupKey] = [];
      }
      groups[groupKey].push(item);
    });

    // Remove empty groups
    Object.keys(groups).forEach(key => {
      if (groups[key].length === 0) {
        delete groups[key];
      }
    });

    return groups;
  }, [items, checklist.phases]);

  // Phase/category labels - will use phase definitions if available
  const getGroupLabel = useCallback((groupKey) => {
    // Check if checklist has phase definitions from UNIFIED_CHECKLISTS
    const phases = checklist.phases || [];
    const phaseObj = phases.find?.(p => p === groupKey || p?.id === groupKey);

    // Common labels for phases and categories
    const labels = {
      // Phases
      planning: 'Planning',
      preparation: 'Preparation',
      execution: 'Execution',
      monitoring: 'Monitoring',
      completion: 'Completion',
      departure: 'Departure',
      pre_watch: 'Pre-Watch / Entry',
      main_systems: 'Main Systems',
      auxiliary_systems: 'Auxiliary Systems',
      approach: 'Approach & Line Handling',
      securing: 'Securing',
      // Categories
      navigation: 'Navigation',
      communications: 'Communications',
      safety: 'Safety Equipment',
      vessel: 'Vessel Condition',
      machinery: 'Machinery',
      crew: 'Crew',
      environment: 'Environment',
      equipment: 'Equipment',
      operational: 'Operational',
      general: 'General',
      // Periodic inspection phases
      hatches_doors: 'Hatches & Doors',
      ports_vents: 'Ports & Ventilators',
      drainage: 'Drainage',
      hull_openings: 'Hull Openings',
      propulsion: 'Main Propulsion',
      steering: 'Steering Gear',
      bilge: 'Bilge System',
      fuel: 'Fuel System',
      extinguishers: 'Fire Extinguishers',
      detection: 'Fire Detection',
      hoses: 'Fire Hoses',
      fixed_systems: 'Fixed Systems',
      dampers: 'Dampers & Ventilation',
      structural: 'Structural Fire Protection',
      vhf_dsc: 'VHF & DSC',
      emergency_beacons: 'Emergency Beacons',
      portable_ais: 'Portable & AIS',
      documentation: 'Documentation',
      compass: 'Compass',
      electronic: 'Electronic Navigation',
      visual: 'Visual Aids',
      charts: 'Charts & Publications',
      liferaft: 'Liferaft',
      lifejackets: 'Lifejackets',
      lifebuoys: 'Lifebuoys',
      pyrotechnics: 'Pyrotechnics',
      rescue: 'Rescue Equipment',
      ppe: 'Personal Protective Equipment',
      access: 'Access & Walkways',
      deck: 'Deck Hazards',
      first_aid: 'First Aid & Medical'
    };

    return labels[groupKey] || groupKey.replace(/_/g, ' ').replace(/\b\w/g, l => l.toUpperCase());
  }, [checklist.phases]);

  return (
    <div className="fixed inset-0 z-50 bg-gray-50 overflow-y-auto">
      {/* Sticky Header */}
      <div className="sticky top-0 z-10 bg-white border-b border-gray-200 shadow-sm">
        <div className="px-4 py-4 sm:px-6">
          <div className="flex items-center gap-4">
            <button
              onClick={onClose}
              className="p-2 text-gray-400 hover:text-gray-600 hover:bg-gray-100 rounded-lg"
            >
              <X className="h-5 w-5" />
            </button>
            <div className="flex-1">
              <h2 className="text-lg font-semibold text-gray-900">{checklist.name}</h2>
              <p className="text-sm text-gray-500">{checklist.description}</p>
            </div>
            <div className="text-right">
              <p className="text-sm font-medium text-gray-900">
                {completedCount}/{items.length} complete
              </p>
              {checklist.regulatory && (
                <p className="text-xs text-gray-500">{checklist.regulatory}</p>
              )}
            </div>
          </div>
        </div>

        {/* Progress bar */}
        <div className="h-1 bg-gray-100">
          <div
            className="h-full bg-cyan-500 transition-all duration-300"
            style={{ width: `${(completedCount / items.length) * 100}%` }}
          />
        </div>
      </div>

      {/* Context Banner */}
      {linkedContext && (
        <div className="px-4 sm:px-6 pt-4">
          <ContextLinkBanner
            context={linkedContext}
            onClear={() => setLinkedContext(null)}
          />
        </div>
      )}

      {/* Auto-populated info */}
      <div className="px-4 sm:px-6 py-4">
        <div className="bg-slate-50 rounded-xl p-4 grid grid-cols-2 sm:grid-cols-4 gap-4">
          <div className="flex items-center gap-2">
            <Calendar className="h-4 w-4 text-slate-400" />
            <span className="text-sm text-slate-700">{dateString}</span>
          </div>
          <div className="flex items-center gap-2">
            <Clock className="h-4 w-4 text-slate-400" />
            <span className="text-sm text-slate-700">{timeString}</span>
          </div>
          <div className="flex items-center gap-2">
            <Ship className="h-4 w-4 text-slate-400" />
            <span className="text-sm text-slate-700">{vessel?.name || 'My Vessel'}</span>
          </div>
          <div className="flex items-center gap-2">
            <User className="h-4 w-4 text-slate-400" />
            <span className="text-sm text-slate-700">{currentUser?.name || 'Crew Member'}</span>
          </div>
        </div>
      </div>

      {/* Checklist Items - Grouped by Phase */}
      <div className="px-4 sm:px-6 pb-32 space-y-6">
        {Object.entries(groupedItems).map(([groupKey, groupItems]) => (
          <div key={groupKey} className="space-y-2">
            {Object.keys(groupedItems).length > 1 && (
              <h3 className="text-sm font-semibold text-gray-700 px-1 py-2 bg-gray-50 rounded-lg flex items-center gap-2">
                <span className="w-2 h-2 rounded-full bg-cyan-500"></span>
                {getGroupLabel(groupKey)}
                <span className="text-xs font-normal text-gray-500 ml-auto">
                  {groupItems.filter(i => checkStates[i.id]?.passed).length}/{groupItems.length}
                </span>
              </h3>
            )}
            <div className="space-y-2">
              {groupItems.map(item => (
                <div
                  key={item.id}
                  className={`bg-white rounded-xl border transition-all ${
                    checkStates[item.id]?.passed
                      ? 'border-green-200 bg-green-50/50'
                      : 'border-gray-200'
                  }`}
                >
                  <button
                    onClick={() => handleToggleCheck(item.id)}
                    className="w-full flex items-start gap-3 p-4"
                  >
                    <div
                      className={`flex-shrink-0 w-6 h-6 rounded-full border-2 flex items-center justify-center transition-colors ${
                        checkStates[item.id]?.passed
                          ? 'bg-green-500 border-green-500'
                          : 'border-gray-300 hover:border-gray-400'
                      }`}
                    >
                      {checkStates[item.id]?.passed && (
                        <Check className="h-4 w-4 text-white" />
                      )}
                    </div>
                    <div className="flex-1 text-left">
                      <p className={`font-medium ${
                        checkStates[item.id]?.passed ? 'text-green-800' : 'text-gray-900'
                      }`}>
                        {item.text}
                      </p>
                      {item.regulation && (
                        <p className="text-xs text-cyan-600 mt-0.5">
                          {item.regulation}
                        </p>
                      )}
                    </div>
                    <div className="flex items-center gap-1">
                      {item.guidance && (
                        <button
                          onClick={(e) => {
                            e.stopPropagation();
                            handleToggleGuidance(item.id);
                          }}
                          className={`p-1.5 rounded-lg transition-colors ${
                            showGuidance[item.id]
                              ? 'bg-amber-100 text-amber-600'
                              : 'text-gray-400 hover:bg-gray-100'
                          }`}
                          title="Show guidance"
                        >
                          <HelpCircle className="h-4 w-4" />
                        </button>
                      )}
                      <button
                        onClick={(e) => {
                          e.stopPropagation();
                          handleToggleNotes(item.id);
                        }}
                        className={`p-1.5 rounded-lg transition-colors ${
                          showNotes[item.id] || checkStates[item.id]?.notes
                            ? 'bg-cyan-100 text-cyan-600'
                            : 'text-gray-400 hover:bg-gray-100'
                        }`}
                        title="Add notes"
                      >
                        <MessageSquare className="h-4 w-4" />
                      </button>
                    </div>
                  </button>

                  {/* Guidance text */}
                  {showGuidance[item.id] && item.guidance && (
                    <div className="px-4 pb-3 -mt-1">
                      <div className="bg-amber-50 border border-amber-100 rounded-lg p-3 text-sm text-amber-800">
                        <div className="flex items-start gap-2">
                          <Info className="h-4 w-4 flex-shrink-0 mt-0.5 text-amber-500" />
                          <p>{item.guidance}</p>
                        </div>
                      </div>
                    </div>
                  )}

                  {/* Notes input */}
                  {showNotes[item.id] && (
                    <div className="px-4 pb-4">
                      <textarea
                        value={checkStates[item.id]?.notes || ''}
                        onChange={(e) => handleItemNotes(item.id, e.target.value)}
                        placeholder="Add notes for this item..."
                        className="w-full px-3 py-2 text-sm border border-gray-200 rounded-lg focus:ring-2 focus:ring-cyan-500 focus:border-cyan-500"
                        rows={2}
                      />
                    </div>
                  )}
                </div>
              ))}
            </div>
          </div>
        ))}

        {/* General Notes */}
        <div className="space-y-2">
          <label className="text-sm font-medium text-gray-700">
            General Notes (optional)
          </label>
          <textarea
            value={generalNotes}
            onChange={(e) => setGeneralNotes(e.target.value)}
            placeholder="Any additional notes or observations..."
            className="w-full px-4 py-3 border border-gray-200 rounded-xl focus:ring-2 focus:ring-cyan-500 focus:border-cyan-500"
            rows={3}
          />
        </div>
      </div>

      {/* Sticky Footer */}
      <div className="fixed bottom-0 left-0 right-0 bg-white border-t border-gray-200 p-4 sm:px-6">
        {error && (
          <div className="mb-3 p-3 bg-red-50 border border-red-200 rounded-lg flex items-center gap-2 text-red-700">
            <AlertTriangle className="h-4 w-4 flex-shrink-0" />
            <span className="text-sm">{error}</span>
          </div>
        )}

        <div className="flex items-center justify-between gap-4">
          <div className="text-sm text-gray-500">
            {!allRequiredChecked ? (
              <span className="text-amber-600">
                {requiredItems.length - requiredItems.filter(i => checkStates[i.id]?.passed).length} required items remaining
              </span>
            ) : (
              <span className="text-green-600 flex items-center gap-1">
                <CheckCircle className="h-4 w-4" />
                All required items complete
              </span>
            )}
          </div>

          <button
            onClick={handleSubmit}
            disabled={!allRequiredChecked || isSubmitting}
            className={`px-6 py-3 rounded-xl font-medium transition-colors ${
              allRequiredChecked && !isSubmitting
                ? 'bg-cyan-600 text-white hover:bg-cyan-700'
                : 'bg-gray-100 text-gray-400 cursor-not-allowed'
            }`}
          >
            {isSubmitting ? 'Saving...' : 'Complete Checklist'}
          </button>
        </div>
      </div>
    </div>
  );
};

export default GenericChecklistExecutionView;
